<template>
  <div
    class="rounded px-2 py-0.5 gap-1 flex items-center text-sm w-fit"
    :class="[type === 'primary' ? properties.bg : properties.border, properties.text]"
  >
    <l-icon v-if="showIcon && properties.icon" :name="properties.icon" />
    <slot>
      {{ properties.content }}
    </slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { LIconName } from './icon.type'
import LIcon from './LIcon.vue'

const { t } = useI18n()

export type Props = {
  type?: 'primary' | 'secondary'
  status: 'active' | 'pending' | 'error' | 'inactive' | 'custom'
  size?: 'small' | 'medium'
  customText?: string
  showIcon?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'primary',
  status: 'active',
  size: 'medium',
  showIcon: true,
  customText: undefined
})

const properties = computed(() => {
  const variations = {
    active: {
      bg: 'bg-g-100',
      text: 'text-g-500',
      border: 'border border-g-500',
      content: props.customText ?? t('status.active'),
      icon: 'check-circle' as LIconName
    },
    pending: {
      bg: 'bg-y-100',
      text: 'text-y-500',
      border: 'border border-y-500',
      content: props.customText ?? t('status.pending'),
      icon: 'alert-circle' as LIconName
    },
    error: {
      bg: 'bg-r-100',
      text: 'text-r-500',
      border: 'border border-r-500',
      content: props.customText ?? t('status.error'),
      icon: 'close-circle' as LIconName
    },
    inactive: {
      bg: 'bg-n-50',
      text: 'text-n-300',
      border: 'border border-n-300',
      content: props.customText ?? t('status.inactive'),
      icon: 'inactive' as LIconName
    },
    custom: {
      bg: 'bg-v-500 dark:bg-v-300',
      text: props.type === 'primary' ? 'text-n-0' : 'text-v-500 dark:text-v-300',
      border: 'border border-v-500 dark:border-v-300',
      content: props.customText,
      icon: undefined
    }
  }
  return variations[props.status]
})
</script>
